
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Trash2, Undo2 } from "lucide-react";

export const DataImporter = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [csvData, setCsvData] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      // Read file content
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setCsvData(content);
      };
      reader.readAsText(selectedFile);
    }
  };

  const parseCsvData = (csvText: string) => {
    const lines = csvText.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    console.log('CSV Headers:', headers);
    
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      
      if (values.length >= headers.length) {
        const record: any = {};
        headers.forEach((header, index) => {
          // Map common header variations to our expected fields
          if (header.includes('commodity') || header.includes('crop')) {
            record.commodity = values[index];
          } else if (header.includes('variety')) {
            record.variety = values[index];
          } else if (header.includes('group') || header.includes('category')) {
            record.group = values[index];
          } else if (header.includes('country')) {
            record.country = values[index];
          } else if (header.includes('region')) {
            record.region = values[index];
          }
        });
        
        // Only add records that have at least one field populated
        if (record.commodity || record.variety || record.group || record.country || record.region) {
          data.push(record);
        }
      }
    }
    
    return data;
  };

  const handleImport = async () => {
    if (!csvData.trim()) {
      toast({
        title: "Error",
        description: "Please paste CSV data or select a file first.",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    try {
      const parsedData = parseCsvData(csvData);
      
      if (parsedData.length === 0) {
        toast({
          title: "Error",
          description: "No valid data found in the CSV. Please check the format.",
          variant: "destructive",
        });
        return;
      }

      console.log(`Parsed ${parsedData.length} records for import`);

      const { data, error } = await supabase.functions.invoke('import-crop-data', {
        body: { data: parsedData },
      });

      if (error) {
        throw error;
      }

      console.log('Import response:', data);

      toast({
        title: "Success",
        description: data.message || `Successfully imported ${parsedData.length} records.`,
      });

      // Clear the form
      setCsvData("");
      setFile(null);
      
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Error",
        description: `Failed to import data: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleClearData = async () => {
    if (!confirm("Are you sure you want to delete all existing crop data? This action cannot be undone.")) {
      return;
    }

    setIsClearing(true);
    try {
      const { data, error } = await supabase.functions.invoke('import-crop-data', {
        body: {},
        method: 'DELETE'
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "All crop data has been cleared successfully.",
      });
      
    } catch (error) {
      console.error('Clear error:', error);
      toast({
        title: "Error",
        description: `Failed to clear data: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  const handleUndoLastImport = async () => {
    setIsUndoing(true);
    try {
      const { data, error } = await supabase.functions.invoke('import-crop-data', {
        body: { action: 'undo_last' },
        method: 'DELETE'
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: data?.message || "Last import has been undone.",
      });
    } catch (error: any) {
      console.error('Undo error:', error);
      toast({
        title: "Error",
        description: `Failed to undo last import: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsUndoing(false);
    }
  };

   return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Crop Data Importer
        </CardTitle>
        <CardDescription>
          Import crop data from CSV file or paste CSV data directly. Expected columns: commodity, variety, group, country, region (region optional)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Upload CSV File
          </label>
          <Input
            type="file"
            accept=".csv,.txt"
            onChange={handleFileChange}
            className="mb-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Or Paste CSV Data
          </label>
          <Textarea
            placeholder="Paste your CSV data here..."
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
            rows={10}
            className="font-mono text-sm"
          />
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={handleImport} 
            disabled={isImporting || !csvData.trim()}
            className="flex-1"
          >
            {isImporting ? "Importing..." : "Import Data"}
          </Button>

          <Button 
            onClick={handleUndoLastImport}
            disabled={isUndoing}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <Undo2 className="h-4 w-4" />
            {isUndoing ? "Undoing..." : "Undo Last Import"}
          </Button>
          
          <Button 
            onClick={handleClearData} 
            disabled={isClearing}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isClearing ? "Clearing..." : "Clear All Data"}
          </Button>
        </div>

        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
          <strong>CSV Format Examples:</strong><br />
          <div className="mt-2 space-y-2">
            <div>
              <strong>Basic format:</strong><br />
              <code>commodity,variety,group,country,region</code><br />
              <code>Tomato,Cherry Tomato,Vegetables,United States,California</code><br />
              <code>Rice,Basmati,Grains,India,Punjab</code><br />
              <code>Coffee,Arabica,Beverages,Brazil,Minas Gerais</code>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
